<template>
  <div :class="sticky && 'sticky-status-card'">
    <v-card outlined class="mb-4">
      <v-card-text class="py-3">
        <div class="d-flex align-center justify-space-between flex-wrap">
          <div class="d-flex align-center gap-2 flex-wrap">
            <h2 class="subtitle-2 font-weight-medium">Publication Status:</h2>
            <div class="d-flex align-center">
              <v-chip :color="status_color" :text-color="status_text_color" class="mr-2" small>
                <v-icon left small>{{ status_icon }}</v-icon>
                <strong>{{ status_text }}</strong>
              </v-chip>
              <span v-if="publish_time" class="caption text--secondary">
                Published {{ formatted_publish_time }}
              </span>
            </div>
          </div>

          <div class="mt-2 mt-sm-0 d-flex align-center">
            <v-menu v-if="has_draft_changes" :close-on-content-click="false" offset-y left>
              <template #activator="{ on, attrs }">
                <v-tooltip bottom>
                  <template #activator="{ on: onTooltip, attrs: attrsTooltip }">
                    <v-btn
                      icon
                      v-bind="{ ...attrs, ...attrsTooltip }"
                      v-on="{ ...on, ...onTooltip }"
                    >
                      <v-badge
                        color="info"
                        :content="change_descriptions.length"
                        :value="change_descriptions.length > 0"
                        overlap
                      >
                        <v-icon>mdi-file-document-edit-outline</v-icon>
                      </v-badge>
                    </v-btn>
                  </template>
                  <span>View Unpublished Changes</span>
                </v-tooltip>
              </template>

              <v-card max-width="400">
                <v-card-title class="subtitle-2 py-2 primary white--text">
                  Unpublished Changes
                </v-card-title>
                <v-card-text class="pt-3" v-if="change_descriptions.length > 0">
                  <ul class="d-flex flex-column gap-1 pl-5">
                    <li
                      v-for="(change, i) in change_descriptions"
                      :key="i"
                      class="text-body-2 mb-0"
                    >
                      {{ change }}
                    </li>
                  </ul>
                </v-card-text>
                <template v-if="is_published">
                  <v-divider></v-divider>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="secondary" text small @click="showDiscardDialog">
                      <v-icon left small>mdi-undo</v-icon>
                      Discard All Changes
                    </v-btn>
                  </v-card-actions>
                </template>
              </v-card>
            </v-menu>

            <v-tooltip bottom :disabled="validation_warnings.length === 0">
              <template #activator="{ on, attrs }">
                <div v-on="on" v-bind="attrs" class="d-inline-block ml-2">
                  <v-btn
                    color="primary"
                    small
                    @click="showPublishDialog"
                    :disabled="is_archived || validation_warnings.length > 0"
                  >
                    <v-icon left small>mdi-publish</v-icon>
                    Publish Form
                  </v-btn>
                </div>
              </template>
              <span v-if="validation_warnings.length > 0">
                {{ validation_warnings.length }} validation issue{{
                  validation_warnings.length === 1 ? ' is' : 's are'
                }}
                preventing publish. See the Setup Checklist below for details.
              </span>
            </v-tooltip>
          </div>
        </div>
        <div v-if="publish_signature_id" class="mt-2">
          <div class="caption text--secondary">Signature ID: {{ publish_signature_id }}</div>
        </div>
      </v-card-text>

      <!-- Checklist Implementation -->
      <template v-if="include_setup_checklist">
        <v-divider class="mx-4" />

        <v-expansion-panels v-model="setup_progress_panel" flat>
          <v-expansion-panel>
            <v-expansion-panel-header class="py-2 px-4">
              <slot name="checklist-header" :outstanding_steps_count="outstanding_steps_count" :all_steps_complete="all_steps_complete">
                <div class="d-flex align-center">
                  <v-icon left color="primary" size="20">mdi-format-list-checks</v-icon>
                  <h2 class="subtitle-2 font-weight-medium grey--text text--darken-1">Setup Checklist</h2>
                  <v-chip
                    v-if="outstanding_steps_count > 0"
                    color="primary"
                    text-color="white"
                    class="ml-2"
                    x-small
                  >
                    {{ outstanding_steps_count }}
                  </v-chip>
                  <v-icon
                    v-if="all_steps_complete"
                    color="success"
                    size="20"
                    class="ml-2"
                  >
                    mdi-check-circle
                  </v-icon>
                </div>
              </slot>
            </v-expansion-panel-header>
            
            <v-expansion-panel-content class="checklist-content">
              <v-list class="py-0">
                <slot name="checklist-items" />
              </v-list>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </template>

      <!-- Non-Checklist Implementation -->
      <template v-else-if="validation_warnings.length > 0">
        <v-divider />

        <v-expansion-panels v-model="validation_panel" flat tile>
          <v-expansion-panel>
            <v-expansion-panel-header class="error--text issues-panel-header">
              <div class="d-flex align-center">
                Please resolve the following issues before publishing
                <v-chip color="error" text-color="white" class="ml-2" x-small>
                  <strong>
                    {{ validation_warnings.length }}
                  </strong>
                </v-chip>
              </div>
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <ul class="pl-5 validation-list">
                <li v-for="(warning, i) in validation_warnings" :key="i">
                  <div class="d-flex justify-space-between align-center">
                    <div class="d-flex align-start">
                      <v-icon size="18" color="error" aria-hidden="true" class="mr-1">
                        mdi-alert-circle
                      </v-icon>
                      <span class="text-body-2 warning-text">{{ warning.text }}</span>
                    </div>
                    <v-btn
                      color="error"
                      class="ml-3 flex-shrink-0"
                      small
                      text
                      @click="$emit('fix-warning', warning)"
                    >
                      Fix It
                    </v-btn>
                  </div>
                </li>
              </ul>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </template>
    </v-card>

    <!-- Dialogs -->
    <v-dialog v-model="publish_dialog_visible" max-width="600" persistent>
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="primary">mdi-publish</v-icon>
          {{ publish_dialog_title }}
        </v-card-title>

        <v-card-text>
          <v-alert v-if="publish_error" type="error" dense class="mb-4">
            {{ publish_error }}
          </v-alert>
          <p class="body-1 mb-1">
            {{ publish_dialog_description }}
          </p>

          <div :key="dialog_key">
            <slot
              name="publish-agreement"
              :publish_agreement_accepted="publish_agreement_accepted"
              :update_publish_agreement_accepted="value => (publish_agreement_accepted = value)"
            >
              <v-checkbox
                v-model="publish_agreement_accepted"
                label="I confirm that I have reviewed the form settings and authorize publication"
                class="mb-4"
              />
            </slot>
          </div>

          <v-card outlined class="mb-4">
            <v-card-text class="py-2">
              <div class="text-caption font-weight-regular mb-0">Form Details</div>
              <slot name="publish-details">
                <div>
                  <strong>Publication Time:</strong>
                  {{ new Date().toLocaleString() }}
                </div>
              </slot>
            </v-card-text>
          </v-card>
          <p class="body-2 text--secondary mb-0">
            {{ publish_dialog_footer }}
          </p>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelPublish" :disabled="publish_loading">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="confirmPublish"
            :loading="publish_loading"
            :disabled="publish_loading || !publish_agreement_accepted"
          >
            <v-icon left>mdi-pen</v-icon>
            Sign and Publish
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="discard_dialog_visible" max-width="500">
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="primary">mdi-undo</v-icon>
          Discard Changes
        </v-card-title>

        <v-card-text>
          <p class="body-1 mb-4">
            This will discard all draft changes and revert the form to its last published version.
          </p>
          <v-alert color="primary" outlined dense>
            <strong>Warning:</strong>
            This action cannot be undone. All unpublished changes will be lost.
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="discard_loading" @click="cancelDiscard">Cancel</v-btn>
          <v-btn
            color="primary"
            :loading="discard_loading"
            :disabled="discard_loading"
            @click="confirmDiscard"
          >
            <v-icon left>mdi-undo</v-icon>
            Discard Changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
/**
 * Displays the publication status of the widget (draft, published, unpublished changes, archived),
 * along with buttons to publish or discard changes. Shows dialogs for publishing (with agreement)
 * and discarding changes. Also lists unpublished changes and validation warnings.
 */

export default {
  name: 'WidgetPublicationStatus',

  props: {
    is_published: {
      type: Boolean,
      default: false,
    },

    has_draft_changes: {
      type: Boolean,
      default: false,
    },

    is_archived: {
      type: Boolean,
      default: false,
    },

    sticky: {
      type: Boolean,
      default: true,
    },

    publish_time: {
      type: String,
      default: null,
    },

    publish_signature_id: {
      type: String,
      default: null,
    },

    change_descriptions: {
      type: Array,
      default: () => [],
    },

    validation_warnings: {
      type: Array,
      default: () => [],
    },

    publish_dialog_title: {
      type: String,
      default: 'Publish Form',
    },

    publish_dialog_description: {
      type: String,
      default:
        'Publishing this form will make it available and enable submissions. This action requires your digital signature for security and compliance purposes.',
    },

    publish_dialog_footer: {
      type: String,
      default:
        'By clicking "Sign and Publish", you confirm that you have reviewed the form settings and authorize its publication.',
    },

    include_setup_checklist: {
      type: Boolean,
      default: false,
    },

    /**
     * Number of incomplete checklist steps (for badge display)
     * Parent component should calculate this based on their specific checklist
     */
    outstanding_steps_count: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      publish_dialog_visible: false,
      publish_agreement_accepted: false,
      publish_loading: false,
      publish_error: null,
      discard_dialog_visible: false,
      discard_loading: false,
      dialog_key: 0,
      setup_progress_panel: null,
      validation_panel: 0, // panel open by default
    };
  },

  computed: {
    all_steps_complete() {
      return this.outstanding_steps_count === 0;
    },

    status_text() {
      if (this.is_archived) return 'Archived';

      if (this.is_published) {
        if (this.has_draft_changes) {
          return 'Unpublished Changes';
        }
        return 'Published';
      } else {
        return 'Draft';
      }
    },

    status_color() {
      if (this.is_archived) return 'grey';
      if (this.is_published) {
        if (this.has_draft_changes) {
          return 'orange';
        }
        return 'success';
      } else {
        return 'warning';
      }
    },

    status_text_color() {
      return 'white';
    },

    status_icon() {
      if (this.is_archived) return 'mdi-archive';
      if (this.is_published) {
        if (this.has_draft_changes) {
          return 'mdi-alert-circle';
        }
        return 'mdi-check-circle';
      } else {
        return 'mdi-pencil';
      }
    },

    formatted_publish_time() {
      if (!this.publish_time) return '';
      const date = new Date(this.publish_time);
      return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString();
    },
  },

  methods: {
    showPublishDialog() {
      this.publish_error = null;
      this.publish_agreement_accepted = false;
      this.dialog_key += 1; // force re-render of slot content
      this.$nextTick(() => {
        this.publish_dialog_visible = true;
      });
    },

    cancelPublish() {
      this.publish_agreement_accepted = false;
      this.publish_error = null;
      this.$nextTick(() => {
        this.publish_dialog_visible = false;
      });
    },

    confirmPublish() {
      this.publish_loading = true;
      this.$emit('publish', {
        onSuccess: () => {
          this.publish_loading = false;
          this.publish_dialog_visible = false;
          this.publish_agreement_accepted = false;
          this.publish_error = null;
        },
        onError: error => {
          this.publish_loading = false;
          this.publish_error = error;
        },
      });
    },

    showDiscardDialog() {
      this.discard_dialog_visible = true;
    },

    cancelDiscard() {
      this.discard_dialog_visible = false;
    },

    confirmDiscard() {
      this.discard_loading = true;
      this.$emit('discard-changes', {
        onSuccess: () => {
          this.discard_loading = false;
          this.discard_dialog_visible = false;
        },
        onError: () => {
          this.discard_loading = false;
        },
      });
    },

    closeSetupChecklist() {
      this.setup_progress_panel = null;
    },

    /**
     * Open the setup checklist if there are pending items.
     * Should be called by parent component after async data has loaded.
     */
    openSetupChecklistIfPending() {
      if (this.outstanding_steps_count > 0 && this.setup_progress_panel === null) {
        this.setup_progress_panel = 0;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.sticky-status-card {
  position: sticky;
  top: -1px;
  z-index: 2;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.issues-panel-header {
  font-weight: bold;
  min-height: unset;
  padding-left: 1rem;
  padding-right: 1rem;
}

.checklist-content {
  :deep(.v-expansion-panel-content__wrap) {
    padding: 0 16px 6px;
  }
}

.validation-list {
  list-style: none;
  padding-left: 0 !important;

  li:not(:last-child) {
    margin-bottom: 0.5rem;
  }
}
</style>
